type EventCallback = (...args: unknown[]) => void;

type ListenerMap = { [key: string]: EventCallback[] };

export class EventEmitter {
	private readonly listeners: ListenerMap = {};

	public on(eventName: string, cb: EventCallback): EventEmitter {
		if (typeof this.listeners[eventName] === 'undefined') {
			this.listeners[eventName] = [];
		}

		this.listeners[eventName].push(cb);
		return this;
	}

	public addListener(eventName: string, cb: EventCallback): EventEmitter {
		return this.on(eventName, cb);
	}

	public off(eventName: string, cb: EventCallback): EventEmitter {
		const ls = this.listeners[eventName];

		if (!ls) {
			return this;
		}

		for (let i = ls.length; i > 0; --i) {
			if (ls[i] === cb) {
				ls.splice(i, 1);
				break;
			}
		}

		return this;
	}

	public removeListener(eventName: string, cb: EventCallback): EventEmitter {
		return this.off(eventName, cb);
	}

	public once(eventName: string, cb: EventCallback): EventEmitter {
		if (typeof this.listeners[eventName] === 'undefined') {
			this.listeners[eventName] = [];
		}

		const onceWrapper = (...args: unknown[]) => {
			cb(...args);
			this.off(eventName, onceWrapper);
		};

		this.listeners[eventName].push(onceWrapper);
		return this;
	}

	public emit(eventName: string, ...args: unknown[]): boolean {
		const fns = this.listeners[eventName];

		if (!fns) {
			return false;
		}

		fns.forEach((f) => {
			f(...args);
		});
		return true;
	}

	public listenerCount(eventName: string): number {
		const fns = this.listeners[eventName] || [];
		return fns.length;
	}

	public rawListeners(eventName: string): EventCallback[] {
		return this.listeners[eventName];
	}
}
